import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { REGION_PRICES } from "./landingContent";

interface RegionSelectProps {
  id: string;
  value: string;
  onChange: (id: string) => void;
}

const optionLabel = (region: (typeof REGION_PRICES)[number]): string =>
  `${region.flag} ${region.country} · ${region.currency}`;

/**
 * Country picker that always opens downward. A native <select> flips upward
 * when the page is scrolled near the bottom of the screen, so this is a small
 * listbox with the same keyboard behaviour (arrows, Home/End, Enter, Escape).
 */
const RegionSelect: React.FC<RegionSelectProps> = ({ id, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedIndex = Math.max(
    0,
    REGION_PRICES.findIndex((region) => region.id === value),
  );

  const openList = () => {
    setActiveIndex(selectedIndex);
    setOpen(true);
  };

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  // Keep the highlighted option (and the whole list on open) in view.
  useEffect(() => {
    if (!open) return;
    const active = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    active?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  const choose = (index: number) => {
    onChange(REGION_PRICES[index].id);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const last = REGION_PRICES.length - 1;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!open) openList();
        else setActiveIndex((index) => Math.min(index + 1, last));
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!open) openList();
        else setActiveIndex((index) => Math.max(index - 1, 0));
        break;
      case "Home":
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case "End":
        if (open) {
          event.preventDefault();
          setActiveIndex(last);
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (open) choose(activeIndex);
        else openList();
        break;
      case "Escape":
        if (open) {
          event.stopPropagation();
          setOpen(false);
        }
        break;
      default:
    }
  };

  return (
    <div className="al-select" ref={rootRef}>
      <button
        id={id}
        type="button"
        className="al-select__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={handleKeyDown}
      >
        <span>{optionLabel(REGION_PRICES[selectedIndex])}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <ul
          ref={listRef}
          className="al-select__list"
          role="listbox"
          aria-labelledby={id}
        >
          {REGION_PRICES.map((region, index) => (
            <li
              key={region.id}
              role="option"
              aria-selected={region.id === value}
              className={[
                region.id === value ? "is-selected" : "",
                index === activeIndex ? "is-active" : "",
              ].join(" ")}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(index)}
            >
              {optionLabel(region)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RegionSelect;
